import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import api, { fileUrl } from "@/lib/api";
import { Search, ArrowUpRight, Loader2 } from "lucide-react";
import { errorDetail } from "@/lib/utils";

export default function PublicSearch() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [privateMsg, setPrivateMsg] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      // `api` (not bare axios) so the staff token or the showroom customer
      // session goes with the request: the catalog is private and the
      // endpoint answers 401 without one (which used to show "no results").
      api
        .get("/public/search", { params: { q } })
        .then((r) => {
          setPrivateMsg("");
          setItems(r.data.results || []);
        })
        .catch((e) => {
          setItems([]);
          // 401: no staff login and no showroom session (private catalogue)
          setPrivateMsg(e?.response?.status === 401 ? errorDetail(e) || "" : "");
        })
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <PublicLayout hideSearch>
      <section className="mx-auto max-w-5xl px-4 py-8 md:px-8 md:py-14">
        <div className="hed-overline">Catalog · search</div>
        <h1 className="mt-2 max-w-2xl font-display text-2xl font-bold leading-tight text-heda-ink md:text-[30px]">
          Find the right plywood, by name, code, or where you&rsquo;ll use it.
        </h1>

        <div className="relative mt-6">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-heda-mute" />
          <input
            data-testid="public-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Try 'marine ply', 'ST4738', 'kitchen'..."
            className="heda-input bg-white py-3.5 pl-11 text-base shadow-heda placeholder:text-heda-mute md:text-lg"
            autoFocus
          />
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-heda-mute">
              <Loader2 size={18} className="animate-spin text-heda-gold" /> Searching...
            </div>
          ) : items.length === 0 ? (
            <div className="heda-card flex flex-col items-center px-6 py-12 text-center">
              <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-heda-olive-tint">
                <Search size={26} className="text-heda-olive" />
              </div>
              <p className="text-sm text-heda-mute" data-testid="public-search-empty">
                {privateMsg || (q ? "No matching products found." : "No published products yet.")}
              </p>
            </div>
          ) : (
            <div className="hed-stagger grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((p) => (
                <Link
                  to={`/product/${p.code}`}
                  key={p.id}
                  className="heda-card group overflow-hidden transition hover:border-heda-gold hover:shadow-heda"
                  data-testid={`search-result-${p.code}`}
                >
                  <div className="aspect-[4/3] bg-heda-cream overflow-hidden">
                    {p.images?.[0] ? (
                      <img
                        src={fileUrl(p.images[0].storage_path)}
                        alt={p.name}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-heda-olive-tint/50 font-display text-4xl font-bold text-heda-olive">
                        {(p.name || "H")[0]}
                      </div>
                    )}
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center justify-between">
                      <span className="hed-overline">{p.category || "Product"}</span>
                      <ArrowUpRight size={16} className="text-heda-mute transition-colors group-hover:text-heda-gold" />
                    </div>
                    <h3 className="mt-1 font-display text-[17px] font-bold leading-tight text-heda-ink">{p.name}</h3>
                    <div className="mt-1 tabular-nums text-[11.5px] text-heda-mute">{p.code}</div>
                    {p.material && <div className="mt-2 text-[13px] text-heda-sub">{p.material}</div>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
